import React, { useCallback, useContext, useState } from 'react';
import _get from 'lodash/get';
import { Container, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { UserContext } from '../../../Contexts/UserContext';

import Title from '../../../Components/Labels/Title';
import PaymentTypeSwitch from '../../../Components/Switches/BaseSwitch';
import LoginDialog from '../../Login/LoginDialog';

import IndividualPayment from './IndividualPayment/IndividualPayment';
import MultiplePayment from './MultiplePayments/MultiplePayment/MultiplePayment';
import { TAB_PROPS } from './MultiplePayments/MultiplePayment/MultiplePayment';

import {
  INDIVIDUAL_LOAD_INITIAL_STATE,
  MULTIPLE_LOAD_INITIAL_STATE,
  PAYMENT_STEPS
} from '../payment_enums';
import { useEffect } from 'react';
import {
  CurrentStepIndexContext,
  SetCurrentStepIndexContext
} from '../../../Contexts/StepperContext';
import { useForm } from 'react-hook-form';

const PaymentOptions = props => {
  const {
    formId,
    updateStepIndex,
    setUpdateStepIndex,
    setCurrentStep: setVisualStepperIndex,
    setGoBack,
    backToDeferredPayment,
    originRoute,
    setNextButtonText,
    handleElementStatus,
    number,
    setNumber,
    type,
    setType,
    payments,
    handleSetPayment,
    setDisabledNext,
    cardIndividualLoad,
    setCardIndividualLoad,
    cardMultipleLoad,
    setCardMultipleLoad,
    totalIndividualCards,
    totalMultipleLoadCards,
    totalCurrentCards,
    checkedMultiplePayments,
    setCheckedMultiplePayments,
    currentTabIndex,
    setCurrentTabIndex,
    isMultiplePaymentsParam,
    canPayPartially,
    setPaymentValueOption,
    setMethodSelected
  } = props;

  const classes = useStyles();

  const currentStepIndex = useContext(CurrentStepIndexContext);
  const setCurrentStep = useContext(SetCurrentStepIndexContext);

  const [openLoginDialog, setOpenLoginDialog] = useState(false);

  const currentUser = useContext(UserContext);
  const authToken = _get(currentUser, 'token');

  const { handleSubmit } = useForm({});

  const getFirstEnabledTabState = useCallback(() => {
    const firstEnabledTab = TAB_PROPS.find(tab => !tab.disabled);
    const firstEnabledTabId = firstEnabledTab ? firstEnabledTab.id : 0;

    return {
      noChecked: firstEnabledTabId,
      checked: firstEnabledTabId
    };
  }, []);

  const handleToggle = () => {
    if (!currentUser) {
      setOpenLoginDialog(true);
      return;
    }
    setCardIndividualLoad(INDIVIDUAL_LOAD_INITIAL_STATE);
    setCardMultipleLoad(MULTIPLE_LOAD_INITIAL_STATE);
    handleSetPayment([]);

    setCheckedMultiplePayments(prev => {
      const nextCheckedMultiplePayments = !prev;

      if (nextCheckedMultiplePayments) {
        setCurrentTabIndex(getFirstEnabledTabState());
      }

      return nextCheckedMultiplePayments;
    });
  };

  useEffect(() => {
    if (isMultiplePaymentsParam && !currentUser) {
      setOpenLoginDialog(true);
    }
  }, [currentUser, isMultiplePaymentsParam, openLoginDialog]);

  useEffect(() => {
    if (!checkedMultiplePayments) {
      return;
    }

    const selectedTab = TAB_PROPS.find(
      tab => tab.id === currentTabIndex.checked
    );
    if (selectedTab && !selectedTab.disabled) {
      return;
    }

    setCurrentTabIndex(getFirstEnabledTabState());
  }, [
    checkedMultiplePayments,
    currentTabIndex.checked,
    setCurrentTabIndex,
    getFirstEnabledTabState
  ]);

  // footer nav setup
  useEffect(() => {
    setNextButtonText('Siguiente');
    setVisualStepperIndex(currentStepIndex);
    setGoBack(originRoute ? backToDeferredPayment : {});
  }, [
    setGoBack,
    setVisualStepperIndex,
    currentStepIndex,
    setNextButtonText,
    originRoute,
    backToDeferredPayment
  ]);

  useEffect(() => {
    if (updateStepIndex) {
      setUpdateStepIndex(false);
      setDisabledNext(false);
      setCurrentStep(step => step + 1);
    }
  }, [updateStepIndex, setCurrentStep, setDisabledNext, setUpdateStepIndex]);

  const renderCurrentStep = () => {
    if (currentStepIndex === 0) {
      if (checkedMultiplePayments) {
        return (
          <MultiplePayment
            formId={formId}
            setDisabledNext={setDisabledNext}
            payments={payments}
            setPayments={handleSetPayment}
            number={number}
            setNumber={setNumber}
            type={type}
            setType={setType}
            cardIndividualLoad={cardIndividualLoad}
            setCardIndividualLoad={setCardIndividualLoad}
            cardMultipleLoad={cardMultipleLoad}
            setCardMultipleLoad={setCardMultipleLoad}
            totalCurrentCards={totalCurrentCards}
            totalIndividualCards={totalIndividualCards}
            totalMultipleLoadCards={totalMultipleLoadCards}
            currentTabIndex={currentTabIndex}
            setCurrentTabIndex={setCurrentTabIndex}
            authToken={authToken}
          />
        );
      }
      return (
        <IndividualPayment
          formId={formId}
          setCurrentStep={setCurrentStep}
          setDisabledNext={setDisabledNext}
          payment={payments[0] || null}
          setPayment={handleSetPayment}
          number={number}
          setNumber={setNumber}
          type={type}
          setType={setType}
          canPayPartially={canPayPartially}
          setPaymentValueOption={setPaymentValueOption}
          setMethodSelected={setMethodSelected}
          currentUser={currentUser}
        />
      );
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit(handleElementStatus)}>
      <>
        <LoginDialog open={openLoginDialog} setOpen={setOpenLoginDialog} />
        <Container maxWidth="md" className={`${classes.root}`}>
          <Box className={classes.mainInfoContainer}>
            <Title
              text={PAYMENT_STEPS[currentStepIndex].label}
              className={classes.title}
            />
            <PaymentTypeSwitch
              checked={checkedMultiplePayments}
              handleToggle={handleToggle}
              label="Pagos múltiples"
              containerClass={classes.switchContainer}
              controlLabelClass={classes.switchControlLabel}
            />
          </Box>
        </Container>
        {renderCurrentStep()}
      </>
    </form>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(0)
  },
  mainInfoContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      justifyContent: 'normal',
      alignItems: 'normal'
    },
    padding: 0
  },
  title: {
    fontWeight: 600,
    [theme.breakpoints.down('xs')]: {
      fontSize: 14
    }
  },
  switchContainer: {
    [theme.breakpoints.down('xs')]: {
      marginBottom: 16
    }
  },
  switchControlLabel: {
    [theme.breakpoints.up('sm')]: {
      margin: 0
    }
  }
}));

export default PaymentOptions;
